import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

/** Height of the fixed header, so anchored sections don't hide underneath it. */
export const HEADER_OFFSET = 84;

/**
 * Scrolls a section into view, accounting for the fixed header and the
 * visitor's motion preference.
 */
export const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;

  const reduced =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;

  window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });

  // Scrolling alone doesn't move focus, which strands keyboard and screen
  // reader users at the top of the page. Move it, without a second scroll.
  el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
};

/**
 * Returns a click handler for in-page section links that also works from the
 * legal pages: if we're not on the home page it navigates there first, then
 * scrolls once the section has mounted.
 */
export const useSectionLink = () => {
  const history = useHistory();
  const location = useLocation();

  return useCallback(
    (id: string) => () => {
      if (location.pathname === "/") {
        scrollToSection(id);
        return;
      }

      history.push("/");
      // Wait for the lazy-loaded home page to paint before looking for the id.
      window.requestAnimationFrame(() => {
        window.setTimeout(() => scrollToSection(id), 120);
      });
    },
    [history, location.pathname]
  );
};
